import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import InputField from "components/fields/InputField";
import { getTokenPurpose, resetPassword, setupPassword } from "api/auth";

const STRONG_PASSWORD_RULE =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

function validatePassword(value: string): string | null {
  if (!value.trim()) return "New password is required.";
  if (value.length < 8) return "New password must be at least 8 characters.";
  if (!STRONG_PASSWORD_RULE.test(value)) {
    return "New password must include uppercase, lowercase, a number, and a special character.";
  }
  return null;
}

export default function ResetPassword(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token")?.trim() ?? "", [params]);
  // Both the account-setup and forgot-password emails link here; the token's
  // `purpose` claim decides which endpoint to call (server re-validates it).
  const isReset = useMemo(
    () => (token ? getTokenPurpose(token) === "PASSWORD_RESET" : false),
    [token]
  );

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const runValidation = (): boolean => {
    const next: typeof fieldErrors = {};
    const npErr = validatePassword(newPassword);
    if (npErr) next.newPassword = npErr;
    if (!confirmPassword.trim()) {
      next.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword !== newPassword) {
      next.confirmPassword = "Passwords do not match.";
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!token) {
      const message = "This link is missing or invalid. Please request a new one.";
      setFormError(message);
      toast.error(message);
      return;
    }
    if (!runValidation()) return;

    setSubmitting(true);
    setFormError("");
    try {
      const submit = isReset ? resetPassword : setupPassword;
      const message = await submit({
        token,
        newPassword,
        confirmPassword,
      });
      toast.success(message);
      navigate("/auth/sign-in", { replace: true });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to set password";
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="flex w-full flex-col">
      <h4 className="mb-2.5 text-xl font-bold tracking-wider text-[#222]">
        {isReset ? "Reset your password" : "Set your password"}
      </h4>
      <p className="mb-6 ml-1 text-sm text-[#6D6D6D]">
        {isReset
          ? "Create a new strong password for your account."
          : "Create a strong password to activate your account."}
      </p>

      {!!formError && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-[#FFD5D8] bg-[#FFF1F2] px-3 py-2 text-[13px] text-[#FB2C36]"
        >
          {formError}
        </div>
      )}

      <InputField
        variant="auth"
        extra="mb-3"
        label="New password*"
        placeholder="Strong password"
        id="setup-password-new"
        name="newPassword"
        type="password"
        autoComplete="new-password"
        required
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        state={fieldErrors.newPassword ? "error" : undefined}
        aria-invalid={!!fieldErrors.newPassword}
      />
      {fieldErrors.newPassword ? (
        <p className="-mt-2 mb-2 text-xs text-red-600" role="alert">
          {fieldErrors.newPassword}
        </p>
      ) : null}

      <InputField
        variant="auth"
        extra="mb-3"
        label="Confirm password*"
        placeholder="Re-enter new password"
        id="setup-password-confirm"
        name="confirmPassword"
        type="password"
        autoComplete="new-password"
        required
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        state={fieldErrors.confirmPassword ? "error" : undefined}
        aria-invalid={!!fieldErrors.confirmPassword}
      />
      {fieldErrors.confirmPassword ? (
        <p className="-mt-2 mb-2 text-xs text-red-600" role="alert">
          {fieldErrors.confirmPassword}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        aria-busy={submitting}
        className="mt-2 w-full rounded-md bg-[#222] py-2.5 text-xs font-semibold text-white transition duration-200 hover:bg-[#333] active:bg-[#111] disabled:opacity-60"
      >
        {submitting
          ? "Saving..."
          : isReset
          ? "Reset password"
          : "Set password"}
      </button>

      <div className="mt-4 text-center">
        <Link
          to="/auth/sign-in"
          className="text-sm font-medium text-[#222] hover:text-[#333]"
        >
          Back to Sign In
        </Link>
      </div>
    </form>
  );
}
